import React, { useState } from 'react';
import Swal from 'sweetalert2';

import FormInput from '../../Components/FormInput';
import FormTextarea from '../../Components/FormTextarea';

function CancelBooking() {

    const [formData, setFormData] = useState({
        booking_ref: '',
        otp: '',
        remark: '',
        bank_name: '',
        branch_name: '',
        ifsc_code: '',
        account_number: '',
    });

    const [errors, setErrors] = useState({});
    const [otpSent, setOtpSent] = useState(false);
    const [loadingOtp, setLoadingOtp] = useState(false);
    const [loadingSubmit, setLoadingSubmit] = useState(false);

    // =========================
    // VALIDATION
    // =========================

    const validateErrors = (name, value) => {

        let error = '';

        if (!value) {
            error = 'This field is required';
        }

        if (name === 'ifsc_code' && value) {

            if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value.toUpperCase())) {
                error = 'Invalid IFSC Code';
            }
        }

        if (name === 'account_number' && value) {

            if (!/^\d+$/.test(value)) {
                error = 'Only numbers allowed';
            }
        }

        if (name === 'otp' && value) {

            if (!/^\d{6}$/.test(value)) {
                error = 'OTP must be 6 digits';
            }
        }

        return error;
    };

    // =========================
    // HANDLE CHANGE
    // =========================

    const handleChange = (e) => {

        const { name, value } = e.target;

        const updatedData = {
            ...formData,
            [name]: value
        };

        setFormData(updatedData);

        const error = validateErrors(name, value);

        setErrors((prev) => ({
            ...prev,
            [name]: error
        }));
    };

    // =========================
    // FORM VALIDATE
    // =========================

    const formValidate = () => {

        const tempErrors = {};

        Object.keys(formData).forEach((key) => {

            const error = validateErrors(
                key,
                formData[key]
            );

            if (error) {
                tempErrors[key] = error;
            }
        });

        setErrors(tempErrors);

        return Object.keys(tempErrors).length === 0;
    };

    // =========================
    // SEND OTP
    // =========================

    const handleSendOtp = async () => {

        if (!formData.booking_ref) {

            setErrors({
                booking_ref: 'Booking reference is required'
            });

            return;
        }

        try {

            setLoadingOtp(true);

            const res = await fetch(
                'https://orisoftpl.com/backend/api/frontend/cancel-booking/send-otp',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                    body: JSON.stringify({
                        booking_ref: formData.booking_ref
                    }),
                }
            );

            const data = await res.json();

            if (!res.ok) {

                Swal.fire({
                    icon: 'error',
                    title: 'Oops!',
                    text: data.message,
                });

                return;
            }

            setOtpSent(true);

            Swal.fire({
                icon: 'success',
                title: 'OTP Sent!',
                text: data.message,
            });

        } catch (error) {

            Swal.fire({
                icon: 'error',
                title: 'Server Error',
                text: 'Something went wrong',
            });

        } finally {
            setLoadingOtp(false);
        }
    };

    // =========================
    // SUBMIT
    // =========================

    const handleSubmit = async (e) => {

        e.preventDefault();

        const isValid = formValidate();

        if (!isValid) return;

        try {

            setLoadingSubmit(true);

            const res = await fetch(
                'https://orisoftpl.com/backend/api/frontend/cancel-booking/verify',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                    body: JSON.stringify(formData),
                }
            );

            const data = await res.json();

            if (!res.ok) {

                Swal.fire({
                    icon: 'error',
                    title: 'Oops!',
                    text: data.message,
                });

                return;
            }

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: data.message,
            });

            setFormData({
                booking_ref: '',
                otp: '',
                remark: '',
                bank_name: '',
                branch_name: '',
                ifsc_code: '',
                account_number: '',
            });

            setOtpSent(false);

        } catch (error) {

            Swal.fire({
                icon: 'error',
                title: 'Server Error',
                text: 'Something went wrong',
            });

        } finally {
            setLoadingSubmit(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className='my-10'>

            <div className="container mx-auto">

                <h3
                    className="
                        text-3xl
                        text-center
                        font-bold
                        relative
                        pb-2
                        text-black
                        mt-10
                        after:absolute
                        after:content-['']
                        after:left-0
                        after:bottom-0
                        after:right-0
                        after:w-[180px]
                        after:h-1
                        after:mx-auto
                        after:bg-gradient-to-br
                        after:from-[#2f4ea2]
                        after:to-[#00bcd4]
                        after:rounded-md
                    "
                >
                    Cancellation Of Booking
                </h3>

                <div className="bg-white p-6 border border-gray-200 shadow-md shadow-inner w-3/5 mx-auto mt-8">

                    <h5 className="text-xl font-bold text-black mb-4">
                        Cancel Booking
                    </h5>

                    <div className="grid grid-cols-2 gap-4 mb-3">

                        <div>
                            <FormInput
                                label="Booking Reference Number (Note: if you dont remember you ref no then you can it out in your profile page.)"
                                required
                                name="booking_ref"
                                value={formData.booking_ref}
                                onChange={handleChange}
                                error={errors.booking_ref}
                                disabled={otpSent}
                            />
                        </div>

                        <div className="flex items-end gap-2">

                            <button
                                type="button"
                                onClick={handleSendOtp}
                                disabled={loadingOtp || otpSent}
                                className="btn btn-error text-white"
                            >
                                {loadingOtp ? 'Sending...' : 'Get OTP'}
                            </button>

                        </div>

                    </div>

                    {otpSent && (
                        <div className="grid grid-cols-2 gap-4 mb-3">

                            <FormInput
                                label="OTP"
                                required
                                name="otp"
                                value={formData.otp}
                                onChange={handleChange}
                                error={errors.otp}
                            />

                            <FormInput
                                label="Bank Name"
                                required
                                name="bank_name"
                                value={formData.bank_name}
                                onChange={handleChange}
                                error={errors.bank_name}
                            />

                            <FormInput
                                label="Branch Name"
                                required
                                name="branch_name"
                                value={formData.branch_name}
                                onChange={handleChange}
                                error={errors.branch_name}
                            />

                            <FormInput
                                label="IFSC Code"
                                required
                                name="ifsc_code"
                                value={formData.ifsc_code}
                                onChange={handleChange}
                                error={errors.ifsc_code}
                            />

                            <FormInput
                                label="Account Number"
                                required
                                name="account_number"
                                value={formData.account_number}
                                onChange={handleChange}
                                error={errors.account_number}
                            />

                        </div>
                    )}

                    {otpSent && (
                        <div className="mb-3">
                            <FormTextarea
                                label="Remark"
                                required
                                name="remark"
                                value={formData.remark}
                                onChange={handleChange}
                                error={errors.remark}
                            />
                        </div>
                    )}

                    {otpSent && (
                        <div className="text-center mt-5">
                            <button
                                type="submit"
                                disabled={loadingSubmit}
                                className="text-white bg-red-600 rounded-md capitalize btn-md px-8"
                            >
                                {
                                    loadingSubmit
                                        ? 'Please wait...'
                                        : 'Cancel Now'
                                }
                            </button>
                        </div>
                    )}

                </div>

            </div>

        </form>
    );
}

export default CancelBooking;